<template>
  <div class="base-input-wrapper">
    <div class="base-input__container">
      <div v-if="$slots.start" class="base-input__start">
        <slot name="start" />
      </div>
      <ion-input
        v-model="localModelValue"
        :autocomplete="enableAutocomplete ? undefined : 'new-password'"
        :placeholder="props.placeholder"
        label=""
        :type="inputType"
        :class="[
          'base-input',
          {
            'base-input--icon-left': $slots.start,
            'base-input--show-password-button': props.type === 'password',
          },
        ]"
        :clear-on-edit="false"
        v-bind="$attrs"
      />
      <button
        v-if="props.type === 'password'"
        type="button"
        class="base-input__show-password-button"
        @click="state.isShowPassword = !state.isShowPassword"
      >
        <img
          :src="`/assets/icon/eye${
            state.isShowPassword ? '-off' : ''
          }-icon.svg`"
          :alt="`${state.isShowPassword ? 'Hide' : 'Show'} password`"
        />
      </button>
    </div>
    <div v-if="props.errorMsg" class="base-input__error">
      {{ props.errorMsg }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { reactive, computed } from 'vue';
import { IonInput } from '@ionic/vue';
import { withDefaults } from 'vue';
import { TextFieldTypes } from '@ionic/core';

interface Props {
  modelValue: string;
  slot?: string;
  errorMsg?: string;
  placeholder?: string;
  type?: TextFieldTypes;
  enableAutocomplete?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  slot: '',
  errorMsg: '',
  placeholder: '',
  type: 'text',
  enableAutocomplete: false,
});

const emit = defineEmits(['update:modelValue']);

const state = reactive({
  isShowPassword: false,
});

const localModelValue = computed({
  get() {
    return props.modelValue;
  },
  set(newValue) {
    emit('update:modelValue', newValue);
  },
});
const inputType = computed(() => {
  if (props.type === 'password') {
    return state.isShowPassword ? 'text' : 'password';
  }
  return props.type;
});
</script>

<style lang="scss" scoped>
.base-input {
  border: 1px solid #a2afd7;
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
  --color: #172659;
  --placeholder-color: #a2afd7;
  --padding-start: 16px;
  --padding-end: 16px;
  --padding-top: 14px;
  --padding-bottom: 14px;
  --placeholder-opacity: 0.8;

  &--icon-left {
    --padding-start: 38px;
  }

  &--show-password-button {
    --padding-end: 44px;
  }

  &__error {
    font-size: 0.7rem;
    color: var(--ion-color-danger);
    padding-top: 5px;
  }

  &__container {
    width: 100%;
    height: 100%;
    position: relative;
  }

  &__start {
    position: absolute;
    left: 16px;
    display: flex;
    align-items: center;
    height: 100%;
    z-index: 3;
    color: #172659;
  }

  &__show-password-button {
    position: absolute;
    top: 0;
    right: 10px;
    width: 36px;
    height: 100%;
    background-color: transparent;
    z-index: 3;
  }
}

:deep(.label-text-wrapper) {
  display: none !important;
}
</style>
